// src/views/Seguridad/Roles/EditarRoles.jsx
import React from "react";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson } from "../../../utils/http";

/* ============================================================
   HELPERS: carga masiva de permisos (idéntico a AsignarEESS)
============================================================ */
async function fetchAllOptions(buildUrl, mapFn, { signal } = {}) {
  let page = 0;
  let acc = [];

  while (true) {
    const url = buildUrl(page);
    const data = await apiFetchJson(url, { signal });
    const items = data?.items ?? [];

    acc.push(...items.map(mapFn));

    const hasMore = Boolean(data?.hasMore ?? items.length >= (data?.size ?? 200));
    if (!hasMore) break;

    page = (data?.page ?? page) + 1;
  }

  const seen = new Set();
  return acc.filter((x) => (seen.has(x.id) ? false : (seen.add(x.id), true)));
}

/* ============================================================
   TAB PERMISOS
============================================================ */
const TabPermisos = React.forwardRef(function TabPermisos({ rolId, onState }, ref) {
  const [options, setOptions] = React.useState([]);
  const [loadingOpts, setLoadingOpts] = React.useState(false);

  const [selectedIds, setSelectedIds] = React.useState([]);
  const [selectedOrig, setSelectedOrig] = React.useState([]);
  const [loadingSel, setLoadingSel] = React.useState(false);

  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const boxRef = React.useRef(null);

  const [saving, setSaving] = React.useState(false);

  // Cerrar dropdown externos
  React.useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Cargar opciones
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingOpts(true);

        const all = await fetchAllOptions(
          (page) => `${apiRoutes.permisos.list}?page=${page}&size=200`,
          (it) => {
            const id = Number(it.id ?? it.perId ?? it.PER_SQ_COD);
            return {
              id,
              modulo: it.moduloNombre ?? it.modulo ?? "",
              submodulo: it.subModuloNombre ?? it.subModulo ?? "",
              accion: (it.accion ?? "").toString().toUpperCase(),
              label: `${it.moduloNombre ?? ""} – ${it.subModuloNombre ?? ""} – ${it.accion}`,
            };
          }
        );

        if (!cancelled) setOptions(all);
      } finally {
        if (!cancelled) setLoadingOpts(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Cargar permisos actuales del rol
  React.useEffect(() => {
    if (!rolId) return;
    let cancelled = false;

    (async () => {
      try {
        setLoadingSel(true);
        const data = await apiFetchJson(apiRoutes.roles.permisos.list(rolId, 0, 2000));
        const ids = (data?.items ?? []).map((p) => Number(p.id));
        const uniq = Array.from(new Set(ids));

        if (!cancelled) {
          setSelectedIds(uniq);
          setSelectedOrig(uniq);
        }
      } finally {
        if (!cancelled) setLoadingSel(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [rolId]);

  const filtered = React.useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return options;
    return options.filter((x) => (x.label || "").toLowerCase().includes(n));
  }, [options, q]);

  const toggle = (id) =>
    setSelectedIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const changed =
    selectedIds.length !== selectedOrig.length ||
    selectedIds.some((id) => !selectedOrig.includes(id));

  const guardar = React.useCallback(async () => {
    if (!rolId) return;

    const setNow = new Set(selectedIds);
    const setOrig = new Set(selectedOrig);
    const agregar = Array.from(setNow).filter((id) => !setOrig.has(id));
    const quitar = Array.from(setOrig).filter((id) => !setNow.has(id));

    if (agregar.length === 0 && quitar.length === 0) return;

    setSaving(true);
    try {
      await apiFetchJson(apiRoutes.roles.permisos.patch(rolId), {
        method: "PATCH",
        headers: { "Content-Type": "application/json; charset=UTF-8" },
        body: JSON.stringify({ agregar, quitar }),
      });

      // éxito → appFetchJson ya muestra overlay automático
      setSelectedOrig(Array.from(setNow));
    } finally {
      setSaving(false);
    }
  }, [rolId, selectedIds, selectedOrig]);

  React.useImperativeHandle(ref, () => ({ save: guardar }));

  React.useEffect(() => {
    onState?.({
      canSave: changed && !saving && !loadingOpts && !loadingSel,
      saving,
    });
  }, [changed, saving, loadingOpts, loadingSel, onState]);

  const label =
    loadingOpts || loadingSel
      ? "Cargando permisos…"
      : selectedIds.length === 0
      ? "Seleccionar"
      : selectedIds.length === 1
      ? "1 seleccionado"
      : `${selectedIds.length} seleccionados`;

  return (
    <div className="rounded-md border border-gray-200 bg-white px-4 py-4">
      <label className="label-strong">Permisos:</label>

      {/* Selector */}
      <div ref={boxRef} className="relative mt-1">
        <button
          type="button"
          className="input w-full flex items-center text-left pl-3 pr-2"
          onClick={() => setOpen((v) => !v)}
          disabled={loadingOpts || loadingSel || saving}
        >
          <span className="truncate">{label}</span>
          <span className="ml-auto">▾</span>
        </button>

        {open && (
          <div className="absolute z-50 mt-1 w-full border rounded bg-white shadow-xl">
            {/* Filtro */}
            <div className="sticky top-0 z-10 bg-white border-b p-2">
              <input
                placeholder="Filtrar permiso…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="border rounded px-2 py-1 w-full"
              />
            </div>

            {/* Lista */}
            <div className="max-h-[60vh] overflow-auto">
              {filtered.map((op) => {
                const checked = selectedIds.includes(op.id);
                return (
                  <label
                    key={op.id}
                    className="flex items-start gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                  >
                    <input type="checkbox" checked={checked} onChange={() => toggle(op.id)} />
                    <span>{op.label}</span>
                  </label>
                );
              })}

              {filtered.length === 0 && (
                <div className="px-3 py-2 text-sm text-gray-500">Sin coincidencias</div>
              )}
            </div>

            <div className="p-2 border-t flex items-center justify-end bg-white">
              <button
                type="button"
                className="btn-fil"
                onClick={() => setOpen(false)}
                disabled={saving}
              >
                Cerrar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

/* ============================================================
   TAB DATOS DEL ROL
============================================================ */
const TabDatos = React.forwardRef(function TabDatos(
  { pk, origNombre, origTipo, onState },
  ref
) {
  const [nombre, setNombre] = React.useState(origNombre ?? "");
  const [tipo, setTipo] = React.useState(origTipo ?? "CLIENTE");
  const [savingRol, setSavingRol] = React.useState(false);

  React.useEffect(() => setNombre(origNombre ?? ""), [origNombre]);
  React.useEffect(() => setTipo(origTipo ?? "CLIENTE"), [origTipo]);

  const hasChanges =
    (nombre ?? "") !== (origNombre ?? "") || (tipo ?? "") !== (origTipo ?? "");

  const save = React.useCallback(async () => {
    if (!pk) return;

    const n = String(nombre ?? "").trim();
    if (!n) throw new Error("El nombre del rol es obligatorio.");

    const body = {};
    if (n !== (origNombre ?? "")) body.nombre = n;
    if (tipo !== origTipo) body.tipo = tipo;

    if (Object.keys(body).length === 0) return;

    setSavingRol(true);
    try {
      await apiFetchJson(apiRoutes.roles.update(pk), {
        method: "PATCH",
        headers: { "Content-Type": "application/json; charset=UTF-8" },
        body: JSON.stringify(body),
      });

      // overlay automático lo muestra appFetchJson
    } finally {
      setSavingRol(false);
    }
  }, [pk, nombre, tipo, origNombre, origTipo]);

  React.useImperativeHandle(ref, () => ({ save }));

  React.useEffect(() => {
    onState?.({ canSave: hasChanges && !savingRol, saving: savingRol });
  }, [hasChanges, savingRol, onState]);

  return (
    <div className="rounded-md border border-gray-200 bg-white px-4 py-4 space-y-4">
      <div className="form-row items-center gap-2">
        <div className="form-label">Nombre*</div>
        <input
          className="input flex-1"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
        />
      </div>

      <div className="form-row items-center gap-2">
        <div className="form-label">Tipo</div>
        <select className="input flex-1" value={tipo} onChange={(e) => setTipo(e.target.value)}>
          <option value="CLIENTE">Cliente</option>
          <option value="STAFF">Staff</option>
        </select>
      </div>
    </div>
  );
});

/* ============================================================
   CONTENEDOR (TABS + GUARDAR)
============================================================ */
export default function EditarRoles({ initial, onVolver, onGuardar }) {
  const pk = initial?.id;

  const [loading, setLoading] = React.useState(true);
  const [origNombre, setOrigNombre] = React.useState("");
  const [origTipo, setOrigTipo] = React.useState("CLIENTE");

  const [tab, setTab] = React.useState("datos");

  const datosRef = React.useRef(null);
  const permsRef = React.useRef(null);

  const [datosState, setDatosState] = React.useState({ canSave: false, saving: false });
  const [permsState, setPermsState] = React.useState({ canSave: false, saving: false });

  // Carga datos del rol
  React.useEffect(() => {
    let alive = true;
    if (!pk) {
      setLoading(false);
      return;
    }

    (async () => {
      try {
        setLoading(true);
        const data = await apiFetchJson(apiRoutes.roles.editInit(pk));
        if (!alive) return;

        setOrigNombre(data?.nombre ?? "");
        setOrigTipo(data?.staff ? "STAFF" : "CLIENTE");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [pk]);

  const activeState = tab === "datos" ? datosState : permsState;

  const handleGuardar = async () => {
    const ref = tab === "datos" ? datosRef : permsRef;
    if (!ref.current?.save) return;

    await ref.current.save();

    // éxito → appFetchJson muestra modal → cuando se cierre: onGuardar()
    onGuardar();
  };

  return (
    <div className="page max-w-4xl px-4">
      <h2 className="page-title mb-4">
        {`Editar Rol${origNombre ? `: ${origNombre}` : ""}`}
      </h2>

      {loading ? (
        <div className="loading-text py-12">Cargando…</div>
      ) : (
        <>
          <div className="mb-4 flex gap-2">
            <button
              type="button"
              className={`px-3 py-2 rounded-md border ${
                tab === "datos" ? "bg-emerald-100 border-emerald-300" : "bg-white border-gray-200"
              }`}
              onClick={() => setTab("datos")}
            >
              Datos del rol
            </button>

            <button
              type="button"
              className={`px-3 py-2 rounded-md border ${
                tab === "permisos"
                  ? "bg-emerald-100 border-emerald-300"
                  : "bg-white border-gray-200"
              }`}
              onClick={() => setTab("permisos")}
            >
              Permisos
            </button>
          </div>

          {tab === "datos" ? (
            <TabDatos
              ref={datosRef}
              pk={pk}
              origNombre={origNombre}
              origTipo={origTipo}
              onState={setDatosState}
            />
          ) : (
            <TabPermisos ref={permsRef} rolId={pk} onState={setPermsState} />
          )}

          <div className="flex items-center justify-end gap-2 pt-3">
            <button type="button" className="btn-fil" onClick={onVolver}>
              Cancelar
            </button>

            <button
              type="button"
              className="btn-fil disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={!activeState.canSave}
              onClick={handleGuardar}
            >
              Guardar
            </button>
          </div>
        </>
      )}
    </div>
  );
}
